import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  Report_Category,
  ReportTemplateInput,
  ReportTemplateWidgetInput,
} from 'gql/graphql';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AbbCommonModule } from 'app/abb-common.module';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DATE_LOCALE_GB_FORMAT } from '@utils/datetime';
import { REPORT_CATEGORY_DICTIONARY } from '../reports';
import { Overwrite } from '@utils/objects';
import {
  FormGroupControlsOf,
  bindSignalToObservable,
} from '@utils/angular-ext';
import { AbbDragDropListComponent } from '../../../components/abb-drag-drop-list/abb-drag-drop-list.component';
import { OverlayService } from '@services/ui/overlay.service';
import { DashboardSidePanelComponent } from '@pages/dashboards/dashboard-side-panel/dashboard-side-panel.component';
import { identity } from '@utils/functions';

export type InitValues = Overwrite<
  ReportTemplateInput,
  {
    categoryType: null | Report_Category;
  }
>;

@Component({
  selector: 'abb-report-template-form',
  standalone: true,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_LOCALE_GB_FORMAT },
  ],
  template: `
    <form id="report-template-form" [formGroup]="form" (ngSubmit)="onSubmit()">
      <div class="flex flex-col gap-3">
        <h3 class="text-base font-semibold text-abb-font/85">
          {{ 'Report Template Name' | t }}
        </h3>
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Name' | t }}</mat-label>
          <input matInput formControlName="name" />
          @if(form.controls.name.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Category' | t }}</mat-label>
          <mat-select formControlName="categoryType" [panelClass]="'panel'">
            <mat-option [value]="null">-</mat-option>
            @for (option of templateCategoryOptions; track option) {
            <mat-option [value]="option['value']">{{
              option.label
            }}</mat-option>
            }
          </mat-select>
          @if(form.controls.categoryType.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Description' | t }}</mat-label>
          <input matInput formControlName="description" />
          @if(form.controls.description.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        <div class="flex items-center gap-4">
          <div class="grow">
            <mat-label class="text-abb-font">{{ 'Components' | t }}*</mat-label>
          </div>
          <div>
            <abb-button
              variant="clean"
              (onclick)="onOpenComponentPanel('new')"
              [icon]="'mat/add'"
              className="!text-abb-font/60 hover:!text-abb-font/70"
            />
          </div>
        </div>

        <ng-template #itemTemplate let-item let-i="index">
          @if (asWidget(item); as widget){
          <div
            (onclick)="onOpenComponentPanel('edit', i)"
            class="cursor-pointer flex items-center grow gap-4 py-4"
          >
            <div class="grow">
              {{ widget.type }}
            </div>
            <div class="flex items-center gap-2">
              <abb-button
                variant="clean"
                (onclick)="onOpenComponentPanel('clone', i)"
                [icon]="'mat/file_copy'"
                className="!text-abb-font/60 hover:!text-abb-font/70"
              />
              <abb-button
                variant="clean"
                (onclick)="onOpenComponentPanel('edit', i)"
                [icon]="'mat/edit'"
                className="!text-abb-font/60 hover:!text-abb-font/70"
              />
            </div>
          </div>
          }
        </ng-template>
        <abb-drag-drop-list [(list)]="widgets" [template]="itemTemplate">
        </abb-drag-drop-list>
        @if (componentsInvalid()){
        <mat-error>{{ 'This field is required' | t }}</mat-error>
        }
      </div>
    </form>
  `,
  imports: [
    TPipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatDatepickerModule,
    AbbCommonModule,
    AbbDragDropListComponent,
  ],
})
export class ReportTemplateFormComponent implements OnInit {
  asWidget = identity<ReportTemplateWidgetInput>;

  mode = input<'new' | 'edit' | 'clone'>('new');

  initValues = input<InitValues | undefined>();
  abbSubmit = output<ReportTemplateInput>();
  widgets = model<ReportTemplateWidgetInput[]>(
    this.initValues()?.widgets ?? []
  );
  form = new FormGroup<FormGroupControlsOf<Omit<InitValues, 'widgets'>>>({
    name: new FormControl('', [Validators.required]),
    categoryType: new FormControl(null, [Validators.required]),
    description: new FormControl('', [Validators.required]),
  });
  formInvalid = signal(false);
  componentsInvalid = computed(() => this.widgets().length === 0);
  invalid = model(false);

  templateCategoryOptions = Object.values(Report_Category).map((c) => ({
    label: REPORT_CATEGORY_DICTIONARY[c].title,
    value: c,
  }));

  prefix = inject(TRANSLATION_PREFIX);

  ngOnInit() {
    this.formInvalid.set(this.form.invalid);
  }
  constructor(private readonly overlay: OverlayService) {
    effect(
      () => {
        const initValues = this.initValues();
        if (!initValues) {
          return;
        }
        this.form.setValue({
          categoryType: initValues.categoryType,
          name: initValues.name,
          description: initValues.description,
        });
        this.formInvalid.set(this.form.invalid);
        this.widgets.set(initValues.widgets);
      },
      { allowSignalWrites: true }
    );
    bindSignalToObservable(
      this.formInvalid,
      this.form.statusChanges,
      (x) => x === 'INVALID'
    );
    effect(
      () => {
        const componentsInvalid = this.componentsInvalid();
        const formInvalid = this.formInvalid();
        this.invalid.set(componentsInvalid || formInvalid);
      },
      { allowSignalWrites: true }
    );
  }

  onSubmit(): void {
    if (this.invalid()) {
      return;
    }
    this.abbSubmit.emit({
      categoryType: this.form.value.categoryType!,
      description: this.form.value.description!,
      name: this.form.value.name!,
      widgets: this.widgets(),
    });
  }

  onOpenComponentPanel(mode: 'new'): void;
  onOpenComponentPanel(mode: 'edit' | 'clone', index: number): void;
  onOpenComponentPanel(mode: 'new' | 'edit' | 'clone', index?: number): void {
    this.overlay
      .openSidePanel(DashboardSidePanelComponent, {
        dashboardId: '', // TODO: remove once DashboardSidePanelComponent is split in two (check other todo)
        mode,
        widgets: [],
        widget:
          mode === 'new'
            ? undefined
            : {
                ...this.widgets()[index!],
                // HACK: reuse the dashboard implementation as much as possible
                id: 'dummy' /* TODO: FIXME: once DashboardSidePanelComponent is split in two we won't need this hack */,
              },
        submitOverride: ({ sharedVariables, display }) =>
          mode === 'new' || mode === 'clone'
            ? this.widgets.update((cur) => [
                ...cur,
                {
                  ...sharedVariables,
                  display,
                  row: 1,
                  col: 1,
                  rowSpan: 1,
                  colSpan: 1,
                } as any, // TODO: FIXME
              ])
            : this.widgets.update((cur) => {
                const curClone = cur.slice();
                curClone[index!] = {
                  ...sharedVariables,
                  display,
                  row: 1,
                  col: 1,
                  rowSpan: 1,
                  colSpan: 1,
                } as any; // TODO: FIXME
                return curClone;
              }),
      })
      .componentRef.setInput('hasBackdrop', false);
  }
}
